import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useEditing } from '@src/state/editing/hooks';
import { usePAARegistration } from '@src/state/PAARegistration';
import { useFormState } from '@src/state/form/hooks';
import { setApimData } from '@src/state/form/actions';
import { CheckboxTree } from '@uhg-abyss/web/ui/CheckboxTree';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { superConnectorApis, SCPAR, SCDS } from '@src/app/constants/apiList';

export const getSuperConnectorApisWithToggles = (featureToggles) => {
  const base = [];
  if (featureToggles.getRow('US8482146_SUPER_CONNECTOR')?.active) {
    base.push(...superConnectorApis);
  }
  if (featureToggles.getRow('US9331102_SUPER_CONNECTOR_SCPAR')?.active) {
    base.push(SCPAR);
  }
  if (featureToggles.getRow('US9778566_SUPER_CONNECTOR_SCDS')?.active) {
    base.push(SCDS);
  }
  return base;
};

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
});

const BoxHeader = styled('div', {
  position: 'relative',
  marginBottom: '10px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const Description = styled('p', {
  fontSize: '16px',
  fontWeight: 500,
  marginBottom: '30px',
  fontFamily: '$primary',
});

const BackButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const NextButton = styled('div', {
  float: 'right',
  width: '115px',
  padding: '6px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$primary1',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const BackButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '12px',
  fontSize: '16px',
});

const NextButtonText = styled('text', {
  color: '$white',
  fontWeight: 'bold',
  marginRight: '12px',
  fontSize: '16px',
});

const ErrorText = styled('div', {
  color: '$redDvz1',
  width: '100%',
  marginBottom: '15px',
  marginTop: '15px',
  fontSize: '14px',
  fontWeight: '600',
});

const TextAreaLabel = styled('div', {
  display: 'inline-block',
  justifyContent: 'space-between',
});

export const APIAccessV2 = ({ handleNext, handleBack }) => {
  const { isEditing } = useEditing.state();
  const { actions, state } = useFormState({ setApimData });
  const { setApiUseText, setSelectedApiCodes } = usePAARegistration.actions();
  const { apiListSelection, apiUseText, selectedApiCodes } =
    usePAARegistration.state();
  const [error, setError] = useState('');
  const form = useForm();

  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const isProviderAccessApiEnabled = featureToggles.getRow(
    'US9770521_PROVIDER_API'
  )?.active;

  const [apisChecked, setApisChecked] = useState(selectedApiCodes);

  const displayCheckedApis = isProviderAccessApiEnabled
    ? apisChecked
    : apisChecked.filter((item) => {
        return item !== 'PAC';
      });

  const displayApiList = isProviderAccessApiEnabled
    ? apiListSelection
    : apiListSelection.filter((api) => {
        return api.code !== 'PAC';
      });

  const onNext = (e) => {
    if (displayCheckedApis.length === 0) {
      setError('Please select at least one API.');
      return;
    }
    form.validate('apiaccess.intended-use', (val) => {
      setApiUseText(val);
      setSelectedApiCodes(
        displayCheckedApis.filter((item) => {
          return item !== 'select-all' && item !== 'super-connector';
        })
      );
      handleNext();
    });
  };

  useEffect(() => {
    form.setValue(
      'apiaccess.intended-use',
      state.registration?.apim?.intendedUseText || apiUseText !== ''
        ? apiUseText
        : ''
    );
  }, []);

  const labelText = () => {
    return (
      <React.Fragment>
        <TextAreaLabel>
          Please provide your intended use for this API(s).
        </TextAreaLabel>
      </React.Fragment>
    );
  };

  const dynamicSuperConnectorApis =
    getSuperConnectorApisWithToggles(featureToggles);

  const nodes = [
    {
      id: '1',
      label: 'Select All',
      value: 'select-all',
      children: [
        ...(dynamicSuperConnectorApis.length > 0
          ? [
              {
                value: 'super-connector',
                label: 'Super Connector',
                children: dynamicSuperConnectorApis,
              },
            ]
          : []),
        ...displayApiList,
      ],
    },
  ];

  return (
    <React.Fragment>
      <MainContainer>
        <BoxHeader>
          <BoxHeaderText>Select the APIs you are interested in.</BoxHeaderText>
        </BoxHeader>
        <Description>Please select all that apply</Description>
        <Divider />
        <CheckboxTree
          id="api-access-options"
          nodes={nodes}
          allExpanded
          checked={displayCheckedApis}
          onCheck={(e) => {
            setApisChecked(e);
          }}
        />
        <div>
          {displayCheckedApis.length > 0 && (
            <FormProvider
              state={form}
              style={{
                width: '95%',
                marginTop: '10px',
              }}
            >
              <TextInputArea
                id="api-intended-txt-id"
                aria-label="API intended use"
                label={labelText()}
                model="apiaccess.intended-use"
                validators={{
                  required: 'Please provide your intended use.',
                }}
                onBlur={(e) => {
                  return form.validate('apiaccess.intended-use');
                }}
                onChange={(e) => {
                  form.validate('apiaccess.intended-use');
                  actions.setApimData({ intendedUseText: e.target.value });
                }}
                styles={{
                  input: (base) => {
                    return {
                      ...base,
                      resize: 'both !important',
                      minWidth: '580px',
                      maxWidth: '100%',
                      overflowY: 'auto !important',
                      minHeight: '100px',
                      maxHeight: '350px',
                    };
                  },
                }}
                css={{
                  'abyss-text-input-area-label': {
                    fontWeight: '600',
                  },
                }}
                maxLength={1000}
                displayMaxLength
                rows={5}
              />
            </FormProvider>
          )}
          <ErrorText>{error}</ErrorText>
        </div>
      </MainContainer>
      <Flex
        css={{
          'abyss-flex-root': { justifyContent: 'space-between' },
        }}
      >
        <Flex.Content space-50>
          {!isEditing && (
            <BackButton
              onClick={() => {
                return handleBack();
              }}
            >
              <IconSymbol icon="arrow_back" size={20} color="$primary1" />
              <BackButtonText>Back</BackButtonText>
            </BackButton>
          )}
        </Flex.Content>
        <Flex.Content space-50>
          <NextButton onClick={onNext}>
            <NextButtonText>{!isEditing ? 'Next' : 'Save'}</NextButtonText>
            <IconSymbol icon="arrow_forward" size={16} color="$primary2" />
          </NextButton>
        </Flex.Content>
      </Flex>
    </React.Fragment>
  );
};

APIAccessV2.propTypes = {
  handleNext: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
};
